import { useForm } from "react-hook-form";
import React, { createRef, useRef, useEffect, useState } from "react";
import Text from "components/Form/Fields/Text";
import Textarea from "components/Form/Fields/Textarea";
import Select from "components/Form/Fields/Select";
import Checkbox from "components/Form/Fields/Checkbox";
import Checkboxes from "components/Form/Fields/Checkboxes";
import Radios from "components/Form/Fields/Radios";
import File from "components/Form/Fields/File";
import Captcha from "components/Form/Fields/Captcha";
import Button from "components/Button";
import classNames from "lib/classNames";
import Body from "components/Body";
import Hidden from "components/Form/Fields/Hidden";
import useTranslation from "next-translate/useTranslation";
import { ArrowRightIcon } from "@heroicons/react/outline";

export default function Form({
  id,
  dataFields,
  defaultValues = null,
  onSuccess = () => null,
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm();
  const { t } = useTranslation("common");
  const [status, setStatus] = useState();
  const [message, setMessage] = useState("");
  const [required, setRequired] = useState(true); // TODO: use as callback for subcomponents
  const [fields, setFields] = useState([]);
  const [captcha, setCaptcha] = useState([]);
  const [files, setFiles] = useState([]);
  const formRef = createRef();
  const captchaRef = useRef();

  useEffect(() => {
    // JSON to Array
    if (dataFields !== null) {
      const tmpFields = [];
      let tmpCaptcha = null;

      Object.keys(dataFields).forEach((key) => {
        const type = dataFields[key]["#type"];
        const name = dataFields[key]["#webform_key"];

        if (type && type === "hidden" && name && name === "captcha") {
          tmpCaptcha = dataFields[key];
        } else {
          tmpFields.push(dataFields[key]);
        }
      });

      if (tmpFields.length > 0) {
        setFields(tmpFields);
      }

      if (tmpCaptcha !== null) {
        setCaptcha(tmpCaptcha);
      }
    }
  }, [dataFields]);

  useEffect(() => {
    if (status === "success") {
      formRef.current.reset();
      reset({});
    }
  }, [status]);

  function filesHandler(field, file) {
    setFiles([
      ...files,
      {
        field_name: field,
        value: file.fid[0].value,
      },
    ]);
  }

  async function onSubmit(data) {
    const originalData = data;

    // Prevent submit spam
    if (status === "submitting") {
      return undefined;
    }

    // Captcha token handling
    if (captcha && captchaRef.current) {
      const captchaToken = await captchaRef.current.executeAsync();

      if (captchaToken) {
        originalData[captcha["#webform_key"]] = captchaToken;
      }
    }

    setStatus("submitting");
    setMessage("");

    // Files handling
    Object.keys(files).forEach((key) => {
      if (originalData[files[key].field_name] !== undefined) {
        originalData[files[key].field_name] = files[key].value;
      }
    });

    const response = await fetch(`/api/forms/submit`, {
      method: "POST",
      body: JSON.stringify({
        webform_id: id,
        ...originalData,
      }),
    });

    if (response.ok) {
      setStatus("success");
      setMessage(t("form.success"));
      onSuccess();
      return true;
    }

    setStatus("error");
    setMessage(t("form.error"));
    return false;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      {required && <div className="mb-8">{t("form.required_asterisk")}</div>}

      {fields.map((field) => {
        const type = field["#type"];

        switch (type) {
          case "textfield":
          case "email":
          case "tel":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Text register={register} errors={errors} field={field} />
              </div>
            );
          case "textarea":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Textarea register={register} errors={errors} field={field} />
              </div>
            );
          case "select":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Select control={control} errors={errors} field={field} />
              </div>
            );
          case "checkbox":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Checkbox control={control} errors={errors} field={field} />
              </div>
            );
          case "checkboxes":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Checkboxes control={control} errors={errors} field={field} />
              </div>
            );
          case "radios":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Radios control={control} errors={errors} field={field} />
              </div>
            );
          case "managed_file":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <File
                  register={register}
                  errors={errors}
                  field={field}
                  filesHandler={filesHandler}
                />
              </div>
            );
          case "hidden":
            return (
              <div key={field.id} className={type !== "hidden" ? "mb-8" : null}>
                <Hidden
                  register={register}
                  field={field}
                  defaultValue={defaultValues[field["#webform_key"]]}
                />
              </div>
            );
        }

        console.warn(`Unhandled form field type "${type}".`);
        return undefined;
      })}

      {captcha && <Captcha ref={captchaRef} field={captcha} />}

      <div className="flex flex-col md:flex-row md:items-center">
        <div className="flex flex-row items-center space-x-8">
          <Button type="submit" disabled={status === "submitting"}>
            <span className="flex items-center gap-2">
              <span>{t("send")}</span>
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          </Button>

          {status === "submitting" && (
            <svg
              className="animate-spin h-6 w-5 text-gray-300"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden={status !== "submitting"}
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
        </div>

        {message !== "" && (
          <div
            className={classNames(
              status === "success" && "text-green-500",
              status === "error" && "text-red-500",
              "text-sm mt-8 md:mt-0 md:ml-8"
            )}
          >
            <Body value={message} />
          </div>
        )}
      </div>
    </form>
  );
}
